import React from 'react';
import './ClassCriteriaDisplay.css';

/**
 * ClassCriteriaDisplay - Displays class-level criteria in a table format
 * @param {Object} props
 * @param {Array} props.criteria - Array of criteria objects with CriteriaName and Value
 * @param {boolean} props.loading - Loading state
 * @param {string} props.error - Error message
 * @param {string} props.title - Card title
 */
const ClassCriteriaDisplay = ({
  criteria,
  loading,
  error,
  title
}) => {
  if (loading) {
    return (
      <div className="bg-slate-50 rounded-lg p-4 space-y-2">
        <h5 className="text-sm font-semibold text-foreground mb-4">{title}</h5>
        {[...Array(8)].map((_, i) => (
          <div key={i} className="h-4 bg-muted/30 rounded animate-pulse"></div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-slate-50 rounded-lg p-4">
        <h5 className="text-sm font-semibold text-foreground mb-4">{title}</h5>
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <i className="bi bi-info-circle"></i>
          <p className="mb-0">{error}</p>
        </div>
      </div>
    );
  }

  if (!criteria || criteria.length === 0) {
    return (
      <div className="bg-slate-50 rounded-lg p-4">
        <h5 className="text-sm font-semibold text-foreground mb-4">{title}</h5>
        <div className="flex flex-col items-center justify-center py-8 text-center space-y-3">
          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
            <svg className="h-6 w-6 text-muted-foreground" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 16v-4m0-4h.01" />
            </svg>
          </div>
          <div className="space-y-1">
            <p className="text-sm font-semibold text-foreground">Class Criteria Not Specified</p>
            <p className="text-xs text-muted-foreground">Class-level criteria not provided by plan for this indication</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 rounded-lg p-4 space-y-6">
      <h5 className="text-sm font-semibold text-foreground">{title}</h5>
      <div className="bg-white rounded-md border border-border overflow-hidden">
        <div className="max-h-96 overflow-y-auto overflow-x-hidden">
          <table className="w-full table-fixed">
            <tbody className="divide-y divide-border">
              {criteria.map((criterion, index) => (
                <tr key={index} className="hover:bg-muted/20 transition-colors">
                  <td className="px-4 py-3 text-sm font-medium text-foreground w-1/2 break-words overflow-wrap-anywhere">
                    {criterion.CriteriaName}
                  </td>
                  <td className={`px-4 py-3 text-sm w-1/2 break-words overflow-wrap-anywhere ${
                    criterion.Value === 'N/A' ? 'text-muted-foreground italic' : 'text-foreground'
                  }`}>
                    {!criterion.Value || criterion.Value.trim() === '' 
                      ? '—' 
                      : criterion.Value}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default ClassCriteriaDisplay;
